import React, { useState, useEffect } from "react";
import api, { API } from "@/lib/api";
import { useFetch } from "@/hooks/useApi";
import { PageHeader, Loader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const TYPES = ["drivers", "vans", "stops", "earnings", "fuel", "mileage", "tickets", "expenses", "profit"];

export default function Reports() {
  const { data: drivers } = useFetch("/drivers");
  const { data: vans } = useFetch("/vans");
  const [type, setType] = useState("earnings");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [driverId, setDriverId] = useState("");
  const [vanReg, setVanReg] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const qs = () => {
    const p = new URLSearchParams();
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    if (driverId) p.set("driver_id", driverId);
    if (vanReg) p.set("van_reg", vanReg);
    return p.toString();
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${type}?${qs()}`);
      setRows(data.rows);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [type]);

  const exportUrl = (fmt) => `${API}/reports/${type}/export?format=${fmt}&${qs()}`;
  const download = async (fmt) => {
    try {
      const res = await api.get(`/reports/${type}/export?format=${fmt}&${qs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${type}_report.${fmt === "excel" ? "xlsx" : fmt}`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="fp-fade-in">
      <PageHeader title="Reports" subtitle="Filter and export operational & financial data" testId="reports-page">
        <div className="grid grid-cols-3 gap-2 w-full sm:flex sm:w-auto">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => download("csv")} data-testid="export-csv-button"><Icons.FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => download("excel")} data-testid="export-excel-button"><Icons.Sheet className="h-4 w-4 mr-1.5" /> Excel</Button>
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => download("pdf")} data-testid="export-pdf-button"><Icons.FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
        </div>
      </PageHeader>

      <Card className="p-5 mb-6">
        <div className="grid sm:grid-cols-5 gap-4 items-end">
          <div>
            <Label>Report</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1.5 capitalize" data-testid="report-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5" data-testid="report-start-input" /></div>
          <div><Label>To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5" data-testid="report-end-input" /></div>
          <div>
            <Label>Driver</Label>
            <Select value={driverId || "all"} onValueChange={(v) => setDriverId(v === "all" ? "" : v)}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All drivers</SelectItem>{(drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Select value={vanReg || "all"} onValueChange={(v) => setVanReg(v === "all" ? "" : v)}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="All vans" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All vans</SelectItem>{(vans || []).map((v) => <SelectItem key={v.id} value={v.registration}>{v.registration}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="mt-1.5" onClick={run} data-testid="run-report-button"><Icons.Play className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? <Loader /> : !rows || rows.length === 0 ? (
          <EmptyState icon="FileText" title="No data" subtitle="Adjust filters and run the report." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{Object.keys(rows[0]).map((k) => <TableHead key={k}>{k}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} data-testid={`report-row-${i}`}>
                    {Object.values(r).map((v, j) => <TableCell key={j} className="font-mono text-sm">{String(v)}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
